import React, { useState, useEffect } from 'react';
import { FiEdit, FiTrash2, FiArrowLeft } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useUser } from './UserContext';

const BlogPostView = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useUser();
  const { postId } = useParams();
  const navigate = useNavigate();

  const API_BASE_URL = 'https://arush1234sharma-querio-backend.hf.space/api/blog';

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/post/${postId}`);
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch post');
        }
        const data = await response.json();
        setPost(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [postId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
          Post not found
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button
        onClick={() => navigate('/blog')}
        className="flex items-center text-blue-500 mb-6"
      >
        <FiArrowLeft className="mr-2" /> Back to Blog
      </button>

      <div className="flex justify-between items-start mb-6">
        <h1 className="text-3xl font-bold">{post.title}</h1>
        {user && (
          <div className="flex space-x-2">
            <button
              onClick={() => navigate(`/blog/edit/${post.id}`)}
              className="text-gray-500 hover:text-blue-500"
              title="Edit post"
            >
              <FiEdit size={20} />
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center text-sm text-gray-500 mb-8">
        <span>By {post.author}</span>
        <span className="mx-2">•</span>
        <span>Posted on {new Date(post.created_at).toLocaleDateString()}</span>
        {post.updated_at !== post.created_at && (
          <>
            <span className="mx-2">•</span>
            <span>Updated on {new Date(post.updated_at).toLocaleDateString()}</span>
          </>
        )}
      </div>

      {post.cover_image && (
        <div className="mb-8">
          <img 
            src={post.cover_image} 
            alt="Cover" 
            className="w-full h-64 object-cover rounded-lg"
          />
        </div>
      )}

      <div className="prose max-w-none mb-8">
        <ReactMarkdown>
          {post.content}
        </ReactMarkdown>
      </div>

      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {post.tags.map(tag => (
            <span
              key={tag}
              className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-600"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogPostView;